'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from 'react';
import { APP_STORE_URL, detectMobilePlatform, GOOGLE_PLAY_URL, type MobilePlatform } from './store-links';
interface StoreButtonsProps {
  /**
   * `hero` matches the homepage hero CTAs (white rectangle + underlined
   * secondary, Inter). `pill` matches the app's own pill buttons on a dark card.
   */
  variant: 'hero' | 'pill';
  /** Where the click came from, for analytics attribution on the store links. */
  placement: string;
  className?: string;
}
function AppleGlyph(t0) {
  const $ = _c(3);
  const {
    className
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== className) {
    t2 = <svg viewBox="0 0 24 24" aria-hidden="true" className={className} fill="currentColor">{t1}</svg>;
    $[1] = className;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function PlayGlyph(t0) {
  const $ = _c(3);
  const {
    className
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <path d="M22.018 13.298l-3.919 2.218-3.515-3.493 3.543-3.521 3.891 2.202a1.49 1.49 0 0 1 0 2.594zM1.337.924a1.486 1.486 0 0 0-.112.568v21.017c0 .217.045.419.124.6l11.155-11.087L1.337.924zm12.207 10.065l3.258-3.238L3.45.195a1.466 1.466 0 0 0-.946-.179l11.04 10.973zm0 2.067l-11 10.933c.298.036.612-.016.906-.183l13.324-7.54-3.23-3.21z" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== className) {
    t2 = <svg viewBox="0 0 24 24" aria-hidden="true" className={className} fill="currentColor">{t1}</svg>;
    $[1] = className;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
const STORES: Record<MobilePlatform, {
  href: string;
  label: string;
  aria: string;
  Glyph: typeof AppleGlyph;
}> = {
  ios: {
    href: APP_STORE_URL,
    label: 'App Store',
    aria: 'Download the Workman app on the App Store',
    Glyph: AppleGlyph
  },
  android: {
    href: GOOGLE_PLAY_URL,
    label: 'Google Play',
    aria: 'Get the Workman app on Google Play',
    Glyph: PlayGlyph
  }
};
const STYLES = {
  hero: {
    // Same classes as the homepage hero-banner CTAs (size "small").
    primary: 'inline-flex items-center justify-center gap-2 rounded bg-white px-6 py-3 text-xs font-bold uppercase tracking-wider text-neutral-900 transition-colors hover:bg-neutral-200',
    secondary: 'inline-flex items-center justify-center gap-2 border-b-2 border-white px-1 py-3 text-xs font-bold uppercase tracking-wider text-white transition-colors hover:border-white/60 hover:text-white/80',
    glyph: 'h-4 w-4 shrink-0',
    gap: 'gap-4'
  },
  pill: {
    primary: 'inline-flex h-14 items-center gap-3 rounded-full bg-white px-7 font-(family-name:--font-ui) text-[15px] font-bold uppercase tracking-[0.1em] text-neutral-950 transition-colors hover:bg-neutral-200',
    secondary: 'inline-flex h-14 items-center gap-3 rounded-full border border-white/50 px-7 font-(family-name:--font-ui) text-[15px] font-bold uppercase tracking-[0.1em] text-white transition-colors hover:border-white hover:bg-white/10',
    glyph: 'h-5 w-5 shrink-0',
    gap: 'gap-3'
  }
} as const;

/**
 * The store choice. Until the platform is known both links use the primary
 * style. Once the visitor's platform is detected, that store stays primary
 * and the other drops to the secondary style, so the right choice reads first
 * without hiding the other.
 */
export function StoreButtons(t0) {
  const $ = _c(14);
  const {
    variant,
    placement,
    className: t1
  } = t0;
  const className = t1 === undefined ? "" : t1;
  const [platform, setPlatform] = useState(null);
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setPlatform(detectMobilePlatform(navigator));
    };
    t3 = [];
    $[0] = t2;
    $[1] = t3;
  } else {
    t2 = $[0];
    t3 = $[1];
  }
  useEffect(t2, t3);
  let t4;
  if ($[2] !== platform) {
    t4 = platform === "android" ? ["android", "ios"] : ["ios", "android"];
    $[2] = platform;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const order = t4;
  const styles = STYLES[variant];
  const t5 = `flex flex-wrap items-center ${styles.gap} ${className}`;
  let t6;
  if ($[4] !== order || $[5] !== placement || $[6] !== platform || $[7] !== styles.glyph || $[8] !== styles.primary || $[9] !== styles.secondary) {
    t6 = order.map(key => {
      const store = STORES[key];
      const secondary = platform !== null && platform !== key;
      return <a key={key} href={store.href} target="_blank" rel="noopener noreferrer" aria-label={store.aria} data-store={key} data-placement={placement} className={`${secondary ? styles.secondary : styles.primary} focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-current`}><store.Glyph className={styles.glyph} />{store.label}</a>;
    });
    $[4] = order;
    $[5] = placement;
    $[6] = platform;
    $[7] = styles.glyph;
    $[8] = styles.primary;
    $[9] = styles.secondary;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t5 || $[12] !== t6) {
    t7 = <div className={t5}>{t6}</div>;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
}